/**
 * Переводы фичи «Аналитика ЖК» (F2/DEV-801). Подмешиваются в базовые локали
 * через deepMerge в locales/index.ts.
 */
type LocaleOverride = Record<string, unknown>

const kpi = {
  monitored: { ru: 'ЖК под мониторингом', en: 'RCs monitored', vn: 'RC đang theo dõi' },
  sold: { ru: 'Продано за 30 дней', en: 'Sold in 30 days', vn: 'Đã bán trong 30 ngày' },
  absorption: { ru: 'Средний absorption', en: 'Avg. absorption', vn: 'Absorption TB' },
  median: { ru: 'Медиана цены м²', en: 'Median price /m²', vn: 'Giá trung vị /m²' },
  medianSqm: { ru: 'Медиана ₫/м²', en: 'Median ₫/m²', vn: 'Giá trung vị ₫/m²' },
  newUnits: { ru: 'Новые юниты', en: 'New units', vn: 'Căn mới' },
}

// подписи-капшены KPI-карточек списка (макет 429:152)
const kpiCap = {
  monitored: { ru: '21 источник активен', en: '21 sources active', vn: '21 nguồn hoạt động' },
  sold: { ru: 'юнитов снято с продажи', en: 'units removed from sale', vn: 'căn đã gỡ bán' },
  absorption: { ru: 'за 30 дней', en: 'over 30 days', vn: 'trong 30 ngày' },
  median: { ru: '≈ $2 780', en: '≈ $2,780', vn: '≈ $2 780' },
  newUnits: { ru: 'выведено в продажу', en: 'listed for sale', vn: 'đưa ra bán' },
}

// метки бакетов комнатности макета (ключ 4+ → 4plus, точка в i18n-ключе недопустима)
const roomBucket = {
  studio: { ru: 'Студии', en: 'Studios', vn: 'Studio' },
  '1': { ru: '1BR', en: '1BR', vn: '1PN' },
  '2': { ru: '2BR', en: '2BR', vn: '2PN' },
  '3': { ru: '3BR', en: '3BR', vn: '3PN' },
  '4plus': { ru: 'Пентхаусы', en: 'Penthouses', vn: 'Penthouse' },
}

const col = {
  name: { ru: 'ЖК', en: 'RC', vn: 'RC' },
  active: { ru: 'В продаже', en: 'On sale', vn: 'Đang bán' },
  sold: { ru: 'Продано 30д', en: 'Sold 30d', vn: 'Bán 30n' },
  absorption: { ru: 'Absorption', en: 'Absorption', vn: 'Absorption' },
  median: { ru: 'Медиана ₫/м²', en: 'Median ₫/m²', vn: 'Trung vị ₫/m²' },
  delta: { ru: 'Δ цены', en: 'Δ price', vn: 'Δ giá' },
  forecast: { ru: 'Прогноз', en: 'Forecast', vn: 'Dự báo' },
  crawl: { ru: 'Посл. обход', en: 'Last crawl', vn: 'Lần quét cuối' },
}

const tabs = {
  overview: { ru: 'Обзор', en: 'Overview', vn: 'Tổng quan' },
  price: { ru: 'Динамика цен', en: 'Price dynamics', vn: 'Động lực giá' },
  inventory: { ru: 'Инвентарь', en: 'Inventory', vn: 'Tồn kho' },
  history: { ru: 'История цен', en: 'Price history', vn: 'Lịch sử giá' },
}
const inv = {
  total: { ru: 'Всего юнитов', en: 'Total units', vn: 'Tổng số căn' },
  free: { ru: 'Свободно', en: 'Available', vn: 'Còn trống' },
  reserved: { ru: 'Бронь', en: 'Reserved', vn: 'Đã giữ chỗ' },
  sold: { ru: 'Продано', en: 'Sold', vn: 'Đã bán' },
  units: { ru: 'юнитов', en: 'units', vn: 'căn' },
}
const ph = {
  unit: { ru: 'Юнит', en: 'Unit', vn: 'Căn' },
  was: { ru: 'Было', en: 'Was', vn: 'Trước' },
  now: { ru: 'Стало', en: 'Now', vn: 'Sau' },
  delta: { ru: 'Δ', en: 'Δ', vn: 'Δ' },
  when: { ru: 'Когда', en: 'When', vn: 'Khi nào' },
}
// капшены KPI-карточек деталки (макет 429:632)
const detailCap = {
  absorption: { ru: 'за 30 дней', en: 'over 30 days', vn: 'trong 30 ngày' },
  sold: { ru: 'из 66 в начале периода', en: 'of 66 at period start', vn: 'trên 66 đầu kỳ' },
  median: { ru: '≈ $4 920', en: '≈ $4,920', vn: '≈ $4 920' },
  discount: { ru: 'по снижениям цены', en: 'from price drops', vn: 'từ đợt giảm giá' },
  forecast: { ru: 'при текущем темпе', en: 'at current pace', vn: 'theo nhịp hiện tại' },
}

const pick = (l: 'ru' | 'en' | 'vn', obj: Record<string, Record<string, string>>) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v[l]]))

const build = (l: 'ru' | 'en' | 'vn') => ({
  common: {
    navigation: {
      rcAnalytics: { ru: 'Аналитика ЖК', en: 'RC Analytics', vn: 'Phân tích RC' }[l],
      rcModeration: { ru: 'Модерация ЖК', en: 'RC moderation', vn: 'Kiểm duyệt RC' }[l],
      rcSourceData: { ru: 'Данные для ЖК', en: 'RC source data', vn: 'Dữ liệu cho RC' }[l],
    },
  },
  rcAnalytics: {
    title: { ru: 'Аналитика ЖК', en: 'RC Analytics', vn: 'Phân tích RC' }[l],
    subtitle: {
      ru: 'Темп реализации и динамика цен по жилым комплексам',
      en: 'Absorption rate and price dynamics by residential complex',
      vn: 'Tốc độ hấp thụ và biến động giá theo khu dân cư',
    }[l],
    export: { ru: 'Экспорт', en: 'Export', vn: 'Xuất' }[l],
    searchPlaceholder: {
      ru: 'Поиск по названию ЖК…',
      en: 'Search by RC name…',
      vn: 'Tìm theo tên RC…',
    }[l],
    filterCity: { ru: 'Город', en: 'City', vn: 'Thành phố' }[l],
    filterDeveloper: { ru: 'Застройщик', en: 'Developer', vn: 'Chủ đầu tư' }[l],
    allCities: { ru: 'Город: Все', en: 'City: All', vn: 'Thành phố: Tất cả' }[l],
    allDevelopers: { ru: 'Застройщик: Все', en: 'Developer: All', vn: 'CĐT: Tất cả' }[l],
    tableTitle: { ru: 'ЖК под мониторингом', en: 'Monitored RCs', vn: 'RC đang theo dõi' }[l],
    sourceNote: {
      ru: 'источник: шахматка застройщика · обход ежедневно в 06:00',
      en: 'source: developer stock chart · daily crawl at 06:00',
      vn: 'nguồn: bảng hàng CĐT · quét hằng ngày lúc 06:00',
    }[l],
    sourceShort: {
      ru: 'источник: шахматка застройщика',
      en: 'source: developer stock chart',
      vn: 'nguồn: bảng hàng CĐT',
    }[l],
    today: { ru: 'сегодня', en: 'today', vn: 'hôm nay' }[l],
    yesterday: { ru: 'вчера', en: 'yesterday', vn: 'hôm qua' }[l],
    errorTitle: { ru: 'Не удалось загрузить', en: 'Failed to load', vn: 'Tải thất bại' }[l],
    emptyTitle: { ru: 'Нет ЖК', en: 'No RCs', vn: 'Không có RC' }[l],
    emptyDescription: {
      ru: 'По текущим фильтрам ничего не найдено',
      en: 'Nothing matches the current filters',
      vn: 'Không có kết quả cho bộ lọc hiện tại',
    }[l],
    months: { ru: 'мес', en: 'mo', vn: 'tháng' }[l],
    pp: { ru: 'пп', en: 'pp', vn: 'đp' }[l],
    crawlFailed: { ru: 'сбой обхода', en: 'crawl failed', vn: 'quét lỗi' }[l],
    shownCount: {
      ru: 'Показано {n} ЖК',
      en: 'Showing {n} RCs',
      vn: 'Hiển thị {n} RC',
    }[l],
    kpi: pick(l, kpi),
    kpiCap: pick(l, kpiCap),
    col: pick(l, col),
    detail: {
      crawl: { ru: 'обход', en: 'crawl', vn: 'quét' }[l],
      absorptionRate: { ru: 'Absorption rate', en: 'Absorption rate', vn: 'Absorption rate' }[l],
      discount: { ru: 'Средний дисконт', en: 'Avg. discount', vn: 'Chiết khấu TB' }[l],
      forecastSellout: { ru: 'Прогноз распродажи', en: 'Sell-out forecast', vn: 'Dự báo bán hết' }[
        l
      ],
      priceDynamics: { ru: 'Динамика цен', en: 'Price dynamics', vn: 'Động lực giá' }[l],
      priceSubtitle: {
        ru: 'Медиана цены ₫ млн/м² · по обходам',
        en: 'Median price ₫ M/m² · by crawl',
        vn: 'Giá trung vị ₫ tr/m² · theo lần quét',
      }[l],
      priceLegend: { ru: 'медиана ₫/м²', en: 'median ₫/m²', vn: 'trung vị ₫/m²' }[l],
      salesPace: { ru: 'Темп реализации', en: 'Sales pace', vn: 'Tốc độ bán' }[l],
      salesPaceSubtitle: {
        ru: 'Юнитов снято с продажи · по неделям',
        en: 'Units removed from sale · weekly',
        vn: 'Số căn gỡ bán · theo tuần',
      }[l],
      salesPaceLegend: { ru: 'снято юнитов', en: 'units removed', vn: 'căn đã gỡ' }[l],
      inventoryStatus: {
        ru: 'Инвентарь по статусам',
        en: 'Inventory by status',
        vn: 'Tồn kho theo trạng thái',
      }[l],
      inventoryStatusNote: {
        ru: 'За 30 дней: продано 18, новых в продаже 5, снижений цены 6',
        en: 'Last 30 days: 18 sold, 5 newly listed, 6 price drops',
        vn: '30 ngày: bán 18, mới đăng 5, giảm giá 6',
      }[l],
      inventoryType: { ru: 'Инвентарь по типам', en: 'Inventory by type', vn: 'Tồn kho theo loại' }[
        l
      ],
      priceHistory: {
        ru: 'История изменений цен',
        en: 'Price change history',
        vn: 'Lịch sử đổi giá',
      }[l],
      priceHistoryNote: {
        ru: 'запись создаётся только при смене цены',
        en: 'recorded only on a price change',
        vn: 'chỉ ghi khi giá thay đổi',
      }[l],
      noSeries: {
        ru: 'Пока мало обходов для графика',
        en: 'Not enough crawls for a chart yet',
        vn: 'Chưa đủ lần quét để vẽ biểu đồ',
      }[l],
      noHistory: {
        ru: 'Изменений цен пока не зафиксировано',
        en: 'No price changes recorded yet',
        vn: 'Chưa ghi nhận thay đổi giá',
      }[l],
      tabs: pick(l, tabs),
      inv: pick(l, inv),
      ph: pick(l, ph),
      cap: pick(l, detailCap),
      roomBucket: pick(l, roomBucket),
    },
    widget: {
      title: { ru: 'Аналитика продаж', en: 'Sales analytics', vn: 'Phân tích bán hàng' }[l],
      more: { ru: 'Подробнее', en: 'Details', vn: 'Chi tiết' }[l],
      absorption: { ru: 'Absorption · 30д', en: 'Absorption · 30d', vn: 'Absorption · 30n' }[l],
      median: { ru: 'Медиана ₫/м²', en: 'Median ₫/m²', vn: 'Trung vị ₫/m²' }[l],
      sold: { ru: 'Продано · 30д', en: 'Sold · 30d', vn: 'Bán · 30n' }[l],
      forecast: { ru: 'Прогноз распродажи', en: 'Sell-out forecast', vn: 'Dự báo bán hết' }[l],
      units: { ru: 'юнитов', en: 'units', vn: 'căn' }[l],
      sparkLabel: {
        ru: 'Медиана цены · 6 недель',
        en: 'Median price · 6 weeks',
        vn: 'Giá trung vị · 6 tuần',
      }[l],
      noDataTitle: {
        ru: 'Данных о продажах пока нет',
        en: 'No sales data yet',
        vn: 'Chưa có dữ liệu bán hàng',
      }[l],
      noDataDesc: {
        ru: 'ЖК в мониторинге, объявлений на Chợ Tốt пока не найдено',
        en: 'RC is monitored, no Chợ Tốt listings matched yet',
        vn: 'RC đang theo dõi, chưa khớp tin đăng Chợ Tốt',
      }[l],
      notMonitoredTitle: {
        ru: 'ЖК не под мониторингом',
        en: 'RC not monitored',
        vn: 'RC chưa được theo dõi',
      }[l],
      notMonitoredDesc: {
        ru: 'Данных о темпе продаж и динамике цен пока нет. Добавьте ЖК в реестр мониторинга harvester.',
        en: 'No sales pace or price data yet. Add the RC to the harvester monitoring registry.',
        vn: 'Chưa có dữ liệu tốc độ bán và giá. Thêm RC vào sổ theo dõi harvester.',
      }[l],
      addToMonitoring: {
        ru: 'Добавить в мониторинг',
        en: 'Add to monitoring',
        vn: 'Thêm vào theo dõi',
      }[l],
    },
    moderation: {
      title: {
        ru: 'Модерация маппингов ЖК',
        en: 'RC mapping moderation',
        vn: 'Kiểm duyệt ánh xạ RC',
      }[l],
      subtitle: {
        ru: 'Очередь projectid-маппингов Chợ Tốt к ЖК реестра: подтвердите, отклоните или выберите другой ЖК',
        en: 'Queue of Chợ Tốt projectid→RC mappings: confirm, reject or pick another RC',
        vn: 'Hàng đợi ánh xạ projectid Chợ Tốt → RC: xác nhận, từ chối hoặc chọn RC khác',
      }[l],
      count: { ru: '{n} на модерации', en: '{n} pending', vn: '{n} chờ duyệt' }[l],
      colProject: { ru: 'projectid', en: 'projectid', vn: 'projectid' }[l],
      colCandidate: { ru: 'Кандидат-ЖК', en: 'Candidate RC', vn: 'RC ứng viên' }[l],
      colConfidence: { ru: 'Уверенность', en: 'Confidence', vn: 'Độ tin cậy' }[l],
      confirm: { ru: 'Подтвердить', en: 'Confirm', vn: 'Xác nhận' }[l],
      reassign: { ru: 'Другой ЖК', en: 'Other RC', vn: 'RC khác' }[l],
      reject: { ru: 'Отклонить', en: 'Reject', vn: 'Từ chối' }[l],
      notInRegistry: { ru: 'нет в реестре', en: 'not in registry', vn: 'không có trong sổ' }[l],
      emptyTitle: { ru: 'Очередь пуста', en: 'Queue is empty', vn: 'Hàng đợi trống' }[l],
      emptyDescription: {
        ru: 'Все projectid-маппинги обработаны',
        en: 'All projectid mappings are processed',
        vn: 'Đã xử lý mọi ánh xạ projectid',
      }[l],
      errorTitle: { ru: 'Не удалось загрузить', en: 'Failed to load', vn: 'Tải thất bại' }[l],
      reassignTitle: { ru: 'Выбрать ЖК', en: 'Pick an RC', vn: 'Chọn RC' }[l],
      reassignSearch: {
        ru: 'Поиск ЖК по названию…',
        en: 'Search RC by name…',
        vn: 'Tìm RC theo tên…',
      }[l],
      reassignEmpty: { ru: 'ЖК не найден', en: 'No RC found', vn: 'Không tìm thấy RC' }[l],
      // Очередь DEV-927/928
      tabHas: { ru: 'Есть в реестре', en: 'In registry', vn: 'Có trong sổ' }[l],
      tabNone: { ru: 'Нет в реестре', en: 'Not in registry', vn: 'Không có trong sổ' }[l],
      searchPlaceholder: {
        ru: 'Поиск по projectid или названию…',
        en: 'Search by projectid or name…',
        vn: 'Tìm theo projectid hoặc tên…',
      }[l],
      filterSource: { ru: 'Источник', en: 'Source', vn: 'Nguồn' }[l],
      filterRegion: { ru: 'Регион', en: 'Region', vn: 'Khu vực' }[l],
      allSources: { ru: 'Все источники', en: 'All sources', vn: 'Mọi nguồn' }[l],
      allRegions: { ru: 'Все регионы', en: 'All regions', vn: 'Mọi khu vực' }[l],
      minListingsAll: { ru: 'Мин. объявлений', en: 'Min. listings', vn: 'Tối thiểu tin' }[l],
      sortConfidence: { ru: 'Уверенность ↓', en: 'Confidence ↓', vn: 'Độ tin cậy ↓' }[l],
      sortListings: { ru: 'Объявлений ↓', en: 'Listings ↓', vn: 'Số tin ↓' }[l],
      sortUpdated: { ru: 'Новизна ↓', en: 'Recency ↓', vn: 'Mới nhất ↓' }[l],
      colRegion: { ru: 'Регион', en: 'Region', vn: 'Khu vực' }[l],
      colListings: { ru: 'Объявл.', en: 'Listings', vn: 'Tin' }[l],
      colSourceName: {
        ru: 'Имя из объявлений',
        en: 'Name from listings',
        vn: 'Tên từ tin đăng',
      }[l],
      dataForComplex: { ru: 'Данные для ЖК', en: 'Data for RC', vn: 'Dữ liệu cho RC' }[l],
    },
    // Экран данных для создания ЖК (DEV-930)
    sourceData: {
      title: {
        ru: 'Данные для создания ЖК',
        en: 'Data for creating an RC',
        vn: 'Dữ liệu để tạo RC',
      }[l],
      subtitle: {
        ru: 'Скопируйте поля и вставьте в форму создания ЖК в админке',
        en: 'Copy the fields and paste them into the RC creation form in admin',
        vn: 'Sao chép các trường và dán vào biểu mẫu tạo RC trong admin',
      }[l],
      back: { ru: 'Назад', en: 'Back', vn: 'Quay lại' }[l],
      backToQueue: { ru: 'К очереди', en: 'To queue', vn: 'Về hàng đợi' }[l],
      errorTitle: { ru: 'Не удалось загрузить', en: 'Failed to load', vn: 'Tải thất bại' }[l],
      retry: { ru: 'Повторить', en: 'Retry', vn: 'Thử lại' }[l],
      listings: { ru: '{n} объявлений', en: '{n} listings', vn: '{n} tin' }[l],
      formFields: {
        ru: 'Поля для формы ЖК',
        en: 'Fields for the RC form',
        vn: 'Trường cho biểu mẫu RC',
      }[l],
      reference: {
        ru: 'Справочно (по объявлениям)',
        en: 'For reference (from listings)',
        vn: 'Tham khảo (từ tin đăng)',
      }[l],
      samples: { ru: 'Примеры объявлений', en: 'Sample listings', vn: 'Tin đăng mẫu' }[l],
      hint: {
        ru: 'Застройщик и точный адрес источник не отдаёт — заполните вручную. Координаты медианные по объявлениям.',
        en: 'The source has no developer or exact address — fill them in manually. Coordinates are median across listings.',
        vn: 'Nguồn không có chủ đầu tư hay địa chỉ chính xác — hãy điền thủ công. Toạ độ là trung vị theo tin đăng.',
      }[l],
      copyAll: { ru: 'Скопировать всё', en: 'Copy all', vn: 'Sao chép tất cả' }[l],
      copyField: { ru: 'Скопировать', en: 'Copy', vn: 'Sao chép' }[l],
      copied: { ru: 'Скопировано', en: 'Copied', vn: 'Đã sao chép' }[l],
      manualFill: { ru: 'заполнить вручную', en: 'fill manually', vn: 'điền thủ công' }[l],
      noCoords: { ru: 'нет координат', en: 'no coordinates', vn: 'không có toạ độ' }[l],
      notOurComplex: {
        ru: 'Это не наш ЖК',
        en: 'Not our RC',
        vn: 'Không phải RC của ta',
      }[l],
      fName: { ru: 'Название', en: 'Name', vn: 'Tên' }[l],
      fDeveloper: { ru: 'Застройщик', en: 'Developer', vn: 'Chủ đầu tư' }[l],
      fRegion: { ru: 'Регион', en: 'Region', vn: 'Khu vực' }[l],
      fCoords: { ru: 'Координаты', en: 'Coordinates', vn: 'Toạ độ' }[l],
      fMedianPrice: { ru: 'Медиана цены', en: 'Median price', vn: 'Giá trung vị' }[l],
      fPpsqm: { ru: 'Медиана ₫/м²', en: 'Median ₫/m²', vn: 'Trung vị ₫/m²' }[l],
      fArea: { ru: 'Площади', en: 'Areas', vn: 'Diện tích' }[l],
      fRooms: { ru: 'Комнаты', en: 'Rooms', vn: 'Phòng' }[l],
      fListings: { ru: 'Объявлений', en: 'Listings', vn: 'Số tin' }[l],
      colListId: { ru: 'ID объявления', en: 'Listing ID', vn: 'ID tin' }[l],
      colPrice: { ru: 'Цена', en: 'Price', vn: 'Giá' }[l],
      colArea: { ru: 'Площадь', en: 'Area', vn: 'Diện tích' }[l],
      colRooms: { ru: 'Комнаты', en: 'Rooms', vn: 'Phòng' }[l],
    },
    // Интерактивный онбординг-тур (DEV-931)
    onboarding: {
      progress: { ru: '{current} из {total}', en: '{current} of {total}', vn: '{current}/{total}' }[
        l
      ],
      skip: { ru: 'Пропустить', en: 'Skip', vn: 'Bỏ qua' }[l],
      back: { ru: 'Назад', en: 'Back', vn: 'Quay lại' }[l],
      next: { ru: 'Далее', en: 'Next', vn: 'Tiếp' }[l],
      done: { ru: 'Готово', en: 'Done', vn: 'Xong' }[l],
      replay: { ru: 'Показать подсказки', en: 'Show hints', vn: 'Xem hướng dẫn' }[l],
      list: {
        steps: {
          toolbar: {
            title: { ru: 'Поиск и фильтры', en: 'Search and filters', vn: 'Tìm kiếm và bộ lọc' }[l],
            description: {
              ru: 'Найдите ЖК по названию, отфильтруйте по городу и застройщику.',
              en: 'Find an RC by name, filter by city and developer.',
              vn: 'Tìm RC theo tên, lọc theo thành phố và chủ đầu tư.',
            }[l],
          },
          kpis: {
            title: { ru: 'Сводка по рынку', en: 'Market summary', vn: 'Tổng quan thị trường' }[l],
            description: {
              ru: 'ЖК под мониторингом, продано за 30 дней, средний absorption, медиана цены и новые юниты.',
              en: 'RCs monitored, sold in 30 days, average absorption, median price and new units.',
              vn: 'RC theo dõi, đã bán trong 30 ngày, absorption TB, giá trung vị và căn mới.',
            }[l],
          },
          table: {
            title: { ru: 'Метрики по ЖК', en: 'RC metrics', vn: 'Chỉ số theo RC' }[l],
            description: {
              ru: 'Колонки: «В продаже» и «Продано 30д» — юниты; «Absorption» — темп реализации; «Медиана ₫/м²» — цена; «Δ цены» — изменение; «Прогноз» — срок распродажи; «Посл. обход» — дата сбора. Клик по строке — деталка с графиками.',
              en: '"On sale"/"Sold 30d" are units; "Absorption" is sales pace; "Median ₫/m²" is price; "Δ price" is change; "Forecast" is sell-out time; "Last crawl" is the collection date. Click a row for details with charts.',
              vn: '«Đang bán»/«Bán 30n» là số căn; «Absorption» là tốc độ bán; «Trung vị ₫/m²» là giá; «Δ giá» là thay đổi; «Dự báo» là thời gian bán hết; «Lần quét cuối» là ngày thu thập. Bấm dòng để xem chi tiết kèm biểu đồ.',
            }[l],
          },
          export: {
            title: { ru: 'Экспорт', en: 'Export', vn: 'Xuất' }[l],
            description: {
              ru: 'Выгрузите текущую таблицу в CSV для анализа в Excel.',
              en: 'Export the current table to CSV for analysis in Excel.',
              vn: 'Xuất bảng hiện tại ra CSV để phân tích trong Excel.',
            }[l],
          },
        },
      },
      moderation: {
        steps: {
          tabs: {
            title: { ru: 'Две вкладки очереди', en: 'Two queue tabs', vn: 'Hai tab hàng đợi' }[l],
            description: {
              ru: '«Есть в реестре» — для projectid найден ЖК-кандидат, подтвердите привязку. «Нет в реестре» — ЖК ещё не заведён.',
              en: '"In registry" — a candidate RC was found for the projectid, confirm the link. "Not in registry" — the RC is not created yet.',
              vn: '«Có trong sổ» — đã tìm thấy RC ứng viên cho projectid, hãy xác nhận. «Không có trong sổ» — RC chưa được tạo.',
            }[l],
          },
          toolbar: {
            title: {
              ru: 'Фильтры и сортировка',
              en: 'Filters and sorting',
              vn: 'Bộ lọc và sắp xếp',
            }[l],
            description: {
              ru: 'Отфильтруйте очередь по источнику, региону и минимуму объявлений; отсортируйте по уверенности, числу объявлений или новизне.',
              en: 'Filter the queue by source, region and minimum listings; sort by confidence, listings count or recency.',
              vn: 'Lọc hàng đợi theo nguồn, khu vực và số tin tối thiểu; sắp xếp theo độ tin cậy, số tin hoặc độ mới.',
            }[l],
          },
          table: {
            title: {
              ru: 'Очередь и действия',
              en: 'Queue and actions',
              vn: 'Hàng đợi và thao tác',
            }[l],
            description: {
              ru: '«projectid» — идентификатор источника; «Уверенность» — насколько система уверена в кандидате; «Кандидат ЖК» — ссылка на объект. Действия: «Подтвердить», «Другой ЖК», «Данные для ЖК» и «Отклонить».',
              en: '"projectid" is the source id; "Confidence" shows how sure the system is about the candidate; "Candidate RC" links to the object. Actions: "Confirm", "Other RC", "Data for RC" and "Reject".',
              vn: '«projectid» là mã nguồn; «Độ tin cậy» cho biết mức độ chắc chắn về ứng viên; «RC ứng viên» liên kết tới đối tượng. Thao tác: «Xác nhận», «RC khác», «Dữ liệu cho RC» và «Từ chối».',
            }[l],
          },
        },
      },
    },
  },
})

export const rcAnalyticsMessages: Record<'ru' | 'en' | 'vn', LocaleOverride> = {
  ru: build('ru'),
  en: build('en'),
  vn: build('vn'),
}
